import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@mui/styles";
import {
  Box,
  LinearProgress,
  Stack,
  linearProgressClasses,
} from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import { ReactComponent as Play } from "../assets/quickView/svg/PlayIcon.svg";
import vasana from "../assets/landing/svg/vasana-white.svg";
import frontLine from "../assets/landing/svg/frontline-white.svg";
import VSNButton from "./VSNButton";
import VSNTypography from "./VSNTypography";
import VSNImage from "./VSNImage";

const useStyles = makeStyles((theme) => ({
  playIcon: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    cursor: "pointer",
    "& .MuiSvgIcon-root": {
      height: 40,
      width: 40,
      fill: "#000000b3",
    },
  },
  logoIcon: {
    position: "absolute",
    top: 10,
    right: 10,
  },
  videoWrapper: {
    borderRadius: 8,
    overflow: "hidden",
    position: "relative",
  },
  video: {
    position: "relative",
    minHeight: "190px",
    width: "100%",
    borderRadius: 8,
    display: "block",
  },
  card: {
    maxWidth: 400,
    borderRadius: 8,
    overflow: "hidden",
  },
  content: {
    // display: "flex",
    // justifyContent: "space-between",
    // alignItems: "center",
  },
  mdHeading: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 1,
    WebkitBoxOrient: "vertical",
    "&.MuiTypography-root": {
      fontSize: 20,
      lineHeight: "24px",
      fontWeight: 500,
    },
  },
  contentDescription: {
    textAlign: "left",
    marginTop: 16,
    "& span": {
      fontSize: 12,
      color: "var(--gray)",
      fontWeight: 500,
      lineHeight: "15px",
    },
  },
  desc: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    "&.MuiTypography-root": {
      fontSize: 14,
      color: "var(--gray)",
      lineHeight: "20px",
      fontWeight: 400,
    },
  },
  statusIndicator: {
    height: 4,
    borderRadius: 2,
    background: "#f50057",
  },
  textBtn: {
    "&.MuiButton-root": {
      padding: 0,
      lineHeight: "16px",
      height: "max-content",
      background: "transparent",
      color: "var(--primary)",
      textDecoration: "underline",
      justifyContent: "start",
      "& .content": {
        fontWeight: 500,
        fontSize: 16,
        [theme.breakpoints.down("sm")]: {
          fontSize: 14,
        },
      },
      "&:hover": {
        background: "transparent",
      },
    },
  },
}));

const VSNVideoDashBoard = ({
  url,
  thumbnail,
  isPlaying,
  onPlay,
  title = "Good and Bad News about People",
  description = "",
}) => {
  const classes = useStyles();
  const videoRef = useRef(null);

  const [progress, setProgress] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [status, setStatus] = useState("Paused");

  const handlePlayandPause = () => {
    if (status === "Playing") {
      videoRef.current.pause();
      setStatus("Paused");
    } else {
      videoRef.current.play();
      setStatus("Playing");
    }
  };

  const checkFullScreen = () => {
    setIsFullScreen(
      Boolean(document.fullscreenElement || document.webkitFullscreenElement)
    );
  };

  const handleVideoEnded = () => {
    setStatus("Paused");
    setProgress(0);
  };

  const handleScroll = () => {
    const video = videoRef.current;
    const rect = video?.getBoundingClientRect();
    const isVisible = rect && rect.top < window.innerHeight && rect.bottom >= 0;
    if (!isVisible) {
      video?.pause();
      setStatus("Paused");
    }
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (video) {
      const { currentTime, duration } = video;
      setProgress((currentTime / duration) * 100);
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.addEventListener("ended", handleVideoEnded);
      video.addEventListener("timeupdate", handleTimeUpdate);
    }

    document.addEventListener("fullscreenchange", checkFullScreen);
    document.addEventListener("webkitfullscreenchange", checkFullScreen);
    window.addEventListener("scroll", handleScroll);

    return () => {
      if (video) {
        video.removeEventListener("ended", handleVideoEnded);
        video.removeEventListener("timeupdate", handleTimeUpdate);
      }
      document.removeEventListener("fullscreenchange", checkFullScreen);
      document.removeEventListener("webkitfullscreenchange", checkFullScreen);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [videoRef]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.style.objectFit =
        isPlaying && isFullScreen ? "contain" : "cover";
    }
  }, [isFullScreen, isPlaying]);

  useEffect(() => {
    const video = videoRef.current;

    if (video) {
      if (isPlaying) {
        video.play();
        setStatus("Playing");
      } else {
        video.pause();
        setStatus("Paused");
      }
    }
  }, [isPlaying]);

  useEffect(() => {
    const video = videoRef.current;

    const handlePlay = () => {
      setStatus("Playing");
      onPlay();
    };

    const handlePause = () => {
      setStatus("Paused");
    };

    if (video) {
      video.addEventListener("play", handlePlay);
      video.addEventListener("pause", handlePause);
    }

    return () => {
      if (video) {
        video.removeEventListener("play", handlePlay);
        video.removeEventListener("pause", handlePause);
      }
    };
  }, [videoRef, onPlay]);

  return (
    <Box>
      <Box className={classes.videoWrapper}>
        <video
          ref={videoRef}
          controls
          playsInline
          controlsList="nodownload"
          preload="metadata"
          poster={thumbnail}
          className={classes.video}
        >
          <source src={url} type="video/mp4" />
          <track src="" kind="captions" label="English" />
        </video>
        {!isPlaying && (
          <Play onClick={handlePlayandPause} className={classes.playIcon} />
        )}
        <Box className={classes.logoIcon}>
          <VSNImage src={vasana} />
        </Box>
      </Box>
      <Stack justifyContent="start" gap={3} className={classes.content}>
        <Box className={classes.contentDescription}>
          <Stack direction="row">
            <PlayCircleOutlineIcon
              sx={{ color: "var(--primary)", m: 0.2 }}
              fontSize="small"
            />
            <VSNTypography
              variant="h6"
              component="h6"
              className={classes.mdHeading}
              color="secondary.darkGrey"
            >
              {title}
            </VSNTypography>
          </Stack>
          <VSNTypography
            variant="body1"
            component="p"
            className={classes.desc}
            mt={0.5}
          >
            {description}
          </VSNTypography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={progress}
          color="primary"
          sx={{
            height: 6,
            borderRadius: 30,
          }}
        />

        <VSNButton
          startIcon={
            status === "Playing" ? (
              <PauseCircleOutlineIcon />
            ) : (
              <PlayCircleOutlineIcon />
            )
          }
          disableRipple
          className={classes.textBtn}
          sx={{
            marginTop: 1.4,
          }}
          handleClick={handlePlayandPause}
        >
          {status === "Playing" ? "Pause" : "Play Now"}

          {/* this play and pause button is not working properly */}
        </VSNButton>
      </Stack>
    </Box>
  );
};

export default VSNVideoDashBoard;
